import { dashBoardCardApi } from "../../api/HttpApis";

// Formatters.
const money = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
const num = (n) => (n == null ? "—" : Number(n).toLocaleString());
const pct = (n) => (n == null ? "—" : `${n}%`);

// "2025-01" -> "Jan 25" (no date lib; avoids timezone parsing).
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (ym) => {
  const [y, m] = String(ym || "").split("-");
  const mi = Number(m) - 1;
  if (!y || mi < 0 || mi > 11) return ym || "";
  return `${MONTHS[mi]} ${String(y).slice(2)}`;
};

// % change of last vs previous point; null when not computable.
export const deltaPct = (series) => {
  if (!Array.isArray(series) || series.length < 2) return null;
  const last = Number(series[series.length - 1]);
  const prev = Number(series[series.length - 2]);
  if (!Number.isFinite(last) || !Number.isFinite(prev) || prev === 0) return null;
  return Math.round(((last - prev) / prev) * 1000) / 10;
};

// Join the three monthly arrays (mixed sort order) into one ASC series.
const buildTrend = (trends = {}) => {
  const byMonth = new Map();
  const put = (arr, key, field) => {
    (arr || []).forEach((r) => {
      const month = r.month;
      if (!month) return;
      if (!byMonth.has(month)) byMonth.set(month, { month, label: monthLabel(month), signups: 0, mau: 0, dau: 0 });
      byMonth.get(month)[key] = Number(r[field]) || 0;
    });
  };
  put(trends.users_by_month, "signups", "user_count");
  put(trends.mau_by_month, "mau", "mau_count");
  put(trends.dau_by_month, "dau", "dau_count");
  return Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
};

// Single call: /dashboard/admin returns everything (summary + metrics + lists).
export const DashboardCardData = async () => {
  let data = {};
  try {
    const response = await dashBoardCardApi();
    data = response?.data?.data || {};
  } catch (error) {
    console.error("Failed to fetch dashboard data:", error.message || error);
    return { groups: [], lists: {}, trend: [], insights: {} };
  }

  const m = data.metrics || {};

  // Cards organized into labeled groups for a scannable dashboard.
  const contentCards = [];
  Object.entries(data).forEach(([key, value]) => {
    if (key === "metrics" || key === "meta") return;
    // Keep the content/catalog counts in the Content group; money/subscriber
    // summary cards move to their proper groups below.
    if (["Movies", "Shows", "Podcasts", "TV Channels", "Short Clips", "Creators"].includes(key)) {
      // The backend's "Creators" figure only counts approved creators (see
      // DashboardController.js) — label it as such to match the Creators
      // tabs (All/Approved/Declined/Under Review/Changes Requested).
      const name = key === "Creators" ? "Creators (Approved)" : key;
      contentCards.push({ name, display: typeof value === "number" ? Math.round(value * 10) / 10 : value });
    }
  });

  const audienceCards = [
    { name: "Total Registered Users", display: num(m.total_registered_users) },
    { name: "Monthly Active Users", display: num(m.monthly_active_users) },
    { name: "Daily Active Users", display: num(m.daily_active_users) },
    { name: "New User Signups (Monthly)", display: num(m.new_user_signups) },
    { name: "Subscriber Growth", display: pct(m.subscriber_growth_rate_percent) },
    { name: "Paid Subscribers", display: num(m.paid_subscribers) },
    { name: "Free Subscribers", display: num(m.free_subscribers) },
    { name: "Active Subscribers", display: num(m.active_subscribers) },
  ];

  // Current calendar period labels so the $0-this-month vs $X-this-year difference
  // is self-explanatory (these cards are NOT controlled by the top range selector).
  const nowD = new Date();
  const monthName = nowD.toLocaleString(undefined, { month: "short" });
  const yearNum = nowD.getFullYear();

  const revenueCards = [
    { name: `Revenue This Month (${monthName})`, display: money(m.revenue_this_month) },
    { name: `Revenue This Year (${yearNum})`, display: money(m.revenue_this_year) },
    { name: "MRR", display: money(m.mrr) },
    { name: "ARR", display: money(m.arr) },
    { name: "ARPU", display: money(m.arpu) },
    { name: "Total Subscriptions (Paid)", display: num(m.paid_subscribers ?? m.active_paid_subscribers) },
    { name: "Trial Users", display: num(m.trial_users) },
    { name: "Churn Rate", display: pct(m.churn_rate_percent) },
    { name: "Customer Lifetime Value", display: money(m.customer_lifetime_value) },
  ];

  const adIntelligenceCards = [
    { name: "Ad Requests", display: num(m.ad_intelligence?.ad_requests) },
    { name: "Impressions (Good)", display: num(m.ad_intelligence?.impressions_good) },
    { name: "Revenue (Channel)", display: money(m.ad_intelligence?.revenue_channel) },
    { name: "eCPM (Channel)", display: money(m.ad_intelligence?.ecpm_channel) },
  ];

  const engagementCards = [
    { name: "Total Watch Time", display: m.total_watch_hours != null ? `${num(m.total_watch_hours)} h` : "—" },
    { name: "Avg Viewing Session", display: m.avg_session_minutes != null ? `${m.avg_session_minutes} min` : "—" },
    { name: "Currently Live Viewers", display: num(m.currently_live_viewers) },
    { name: "Live Viewers (30d)", display: num(m.live_viewers) },
    { name: "On-Demand Viewers (30d)", display: num(m.on_demand_viewers) },
    { name: "Active Live Channels", display: num(m.live_channels_active) },
  ];

  // Accents drawn from the app's own palette (brand copper + existing chip hues).
  const groups = [
    { title: "Content", accent: "#B87333", cards: contentCards },          // brand copper
    { title: "Audience", accent: "#33AFB8", cards: audienceCards },        // chip teal
    { title: "Revenue & Billing", accent: "#33B86F", cards: revenueCards }, // status green
    { title: "Engagement", accent: "#B833B8", cards: engagementCards },    // chip magenta
    { title: "Ad Intelligence", accent: "#E0A030", cards: adIntelligenceCards }, // amber, distinct from revenue green
  ];

  const lists = {
    most_popular_content: m.most_popular_content || [],
    top_devices: m.top_devices || [],
    top_countries: m.top_countries || [],
    top_cities: m.top_cities || [],
    revenue_by_plan: m.revenue_by_plan || [],
    revenue_by_platform: m.revenue_by_platform || [],
  };

  const trend = buildTrend(data.trends);

  // Additional insights derived from existing event/subscription data.
  const insights = {
    subscription_mix: m.subscription_mix || null,
    qoe: m.qoe || null,
    content_performance: m.content_performance || [],
    trending_now: m.trending_now || [],
    new_vs_returning: m.new_vs_returning || null,
    watch_session: m.watch_session || null,
    playback_reliability: m.playback_reliability || null,
    top_searches: m.top_searches || [],
    engagement_funnel: m.engagement_funnel || null,
    admin_overview: m.admin_overview || null,
  };

  return { groups, lists, trend, insights };
};
